import type { ReactNode } from 'react';

import { FileText } from 'lucide-react';

import type { CandidateListItem } from '@/api/schema';
import { Avatar } from '@/components/ui/Avatar';
import { formatDate, type DateFormat } from '@/lib/dates';
import { NOTHING } from '@/lib/nothing';

import { cvUrlFor } from '../api';
import type { CandidateColumnKey } from '../columns';
import { APPLYING_FOR_LABEL, SOURCE_LABEL } from '../labels';
import { PointsMeter } from './PointsMeter';
import { BooleanFlag, DocumentationFlag, RsrFlag } from './StateFlag';
import { FlystStageChip, PaymentChip, StatusChip } from './StatusChip';
import styles from './CandidateCells.module.css';

/** Text, or a faint dash when there is nothing. Never a blank cell. */
function text(value: string | null | undefined): ReactNode {
  return value ? <span>{value}</span> : <span className={styles.none}>{NOTHING}</span>;
}

/**
 * One cell.
 *
 * A switch rather than a map of components, so the compiler can prove it is
 * exhaustive: the `never` at the bottom means a column added to `columns.ts` without a
 * renderer here fails to build, rather than rendering an empty cell nobody notices.
 */
export function renderCell(
  column: CandidateColumnKey,
  candidate: CandidateListItem,
  dateFormat: DateFormat,
): ReactNode {
  // What the five tracked chips need to fetch and date their own history (Phase 16).
  const history = { candidateId: candidate.id, dateFormat };

  switch (column) {
    case 'received_on':
      return (
        <span className={styles.mono}>{formatDate(candidate.received_on, dateFormat)}</span>
      );

    case 'full_name':
      return (
        <div className={styles.person}>
          {/* No photo on a candidate, so the initials are the whole avatar. */}
          <Avatar name={candidate.full_name} size={32} />
          <div className={styles.personText}>
            <span className={styles.personName}>{candidate.full_name}</span>
            <span className={styles.personRef}>{candidate.reference}</span>
          </div>
        </div>
      );

    case 'reference':
      return <span className={styles.mono}>{candidate.reference}</span>;

    case 'status':
      return <StatusChip status={candidate.status} history={history} />;

    case 'contacted':
      return <BooleanFlag value={candidate.contacted} />;

    case 'details_shared':
      return <BooleanFlag value={candidate.details_shared} />;

    case 'followup_done':
      return <BooleanFlag value={candidate.followup_done} />;

    case 'flyst_stage':
      return <FlystStageChip stage={candidate.flyst_stage} history={history} />;

    case 'rsr_state':
      return <RsrFlag value={candidate.rsr_state} history={history} />;

    case 'rsr_scheduled_on':
      // formatDate already returns a dash for null, so there is nothing to branch on.
      return (
        <span className={styles.mono}>
          {formatDate(candidate.rsr_scheduled_on, dateFormat)}
        </span>
      );

    case 'documentation':
      return <DocumentationFlag value={candidate.documentation} history={history} />;

    case 'sponsor_points':
      return <PointsMeter value={candidate.sponsor_points} />;

    case 'preferred_country':
      return text(candidate.preferred_country?.name);

    case 'department':
      return text(candidate.department?.name);

    case 'preferred_position':
      return text(candidate.preferred_position?.name);

    case 'location':
      return text([candidate.city, candidate.country].filter(Boolean).join(', '));

    case 'phone':
      // Monospaced so numbers line up when you read down the column (plan 9.3).
      return <span className={styles.mono}>{candidate.phone ?? NOTHING}</span>;

    case 'email':
      return <span className={styles.muted}>{candidate.email ?? NOTHING}</span>;

    case 'source':
      return text(SOURCE_LABEL[candidate.source]);

    case 'applying_for':
      return text(APPLYING_FOR_LABEL[candidate.applying_for]);

    case 'payment_status':
      return <PaymentChip status={candidate.payment_status} history={history} />;

    case 'cv':
      return candidate.cv_file_id ? (
        // A real link, not a button: middle-click and copy-address work, and a
        // top-level GET carries the SameSite=Lax session cookie. The click is stopped
        // from bubbling for Phase 14, when the row itself becomes clickable.
        <a
          className={styles.fileChip}
          href={cvUrlFor(candidate.id, candidate.cv_file_id)}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <FileText size={13} aria-hidden="true" />
          View
        </a>
      ) : (
        <span className={styles.none}>{NOTHING}</span>
      );

    default: {
      const exhaustive: never = column;
      return exhaustive;
    }
  }
}
